interface FooterProps {
	pathname?: string;
}

export function Footer({ pathname = "/" }: FooterProps) {
	const currentYear = new Date().getFullYear();
	const isBlogPage = pathname.startsWith("/blog") || pathname.startsWith("/about");
	const productLinks = isBlogPage
		? [
				{ label: "Home", href: "/" },
				{ label: "Blog", href: "/blog/" },
				{ label: "Pricing", href: "/#pricing" },
				{ label: "Download", href: "/#download" },
			]
		: [
				{ label: "Features", href: "#features" },
				{ label: "FAQ", href: "#faq" },
				{ label: "Pricing", href: "#pricing" },
				{ label: "Blog", href: "/blog/" },
				{ label: "Download", href: "#download" },
			];

	return (
		<footer className="border-t border-white/[0.06] py-12">
			<div className="mx-auto max-w-6xl px-6">
				<div className="grid grid-cols-1 md:grid-cols-5 gap-8">
					{/* Brand */}
					<div className="md:col-span-1">
						<div className="flex items-center gap-2.5 mb-3">
							<div className="w-7 h-7 rounded-lg bg-brand-500 flex items-center justify-center">
								<svg viewBox="0 0 32 32" className="w-4 h-4">
									<circle cx="16" cy="16" r="6" fill="none" stroke="white" strokeWidth="2.5" />
									<circle cx="16" cy="16" r="2" fill="white" />
								</svg>
							</div>
							<span className="text-sm font-bold">Clapio</span>
						</div>
						<p className="text-xs text-white/30 leading-relaxed">
							Beautiful screen recordings in seconds.
						</p>
					</div>

					{/* Product */}
					<div>
						<h4 className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-3">
							Product
						</h4>
						<ul className="space-y-2">
							{productLinks.map((link) => (
								<li key={link.label}>
									<a
										href={link.href}
										className="text-xs text-white/30 hover:text-white/60 transition-colors"
									>
										{link.label}
									</a>
								</li>
							))}
						</ul>
					</div>

					<div>
						<h4 className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-3">
							Compare
						</h4>
						<ul className="space-y-2">
							{[
								{
									label: "Screen Recorder for Mac",
									href: "/screen-recorder-for-mac/",
								},
								{
									label: "Screen Recorder for Windows",
									href: "/screen-recorder-for-windows/",
								},
								{
									label: "Screen Recorder with Webcam",
									href: "/screen-recorder-with-webcam/",
								},
								{
									label: "Screen Recorder with Audio",
									href: "/screen-recorder-with-audio/",
								},
								{
									label: "Camtasia Alternative",
									href: "/camtasia-alternative/",
								},
								{
									label: "OBS Alternative",
									href: "/obs-alternative/",
								},
								{
									label: "Loom Alternative",
									href: "/loom-alternative/",
								},
								{
									label: "QuickTime Alternative",
									href: "/quicktime-player-alternative/",
								},
								{
									label: "Screen & Audio Recorder Windows",
									href: "/screen-and-audio-recorder-windows/",
								},
								{
									label: "Cap Alternative",
									href: "/cap-alternative/",
								},
								{
									label: "Screen Recorder for Linux",
									href: "/screen-recorder-for-linux/",
								},
								{
									label: "Screen Studio Alternative",
									href: "/screen-studio-alternative/",
								},
								{
									label: "Screen Studio for Windows",
									href: "/screen-studio-for-windows/",
								},
								{
									label: "Screen Studio for Linux",
									href: "/screen-studio-for-linux/",
								},
							].map((link) => (
								<li key={link.label}>
									<a
										href={link.href}
										className="text-xs text-white/30 hover:text-white/60 transition-colors"
									>
										{link.label}
									</a>
								</li>
							))}
						</ul>
					</div>

					{/* Support */}
					<div>
						<h4 className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-3">
							Support
						</h4>
						<ul className="space-y-2">
							{[
								{
									label: "About Clapio",
									href: "/about/",
									external: false,
								},
								{
									label: "Report issue",
									href: "https://docs.google.com/forms/d/e/1FAIpQLSe6-JvdMixskej51UvG5IdqbnTW_cqDlIDyQLPtEvvwFvkZZg/viewform?usp=publish-editor",
									external: true,
								},
								{
									label: "Official releases",
									href: "https://github.com/Dhaxor/clapio-release/releases",
									external: true,
								},
							].map((link) => (
								<li key={link.label}>
									<a
										href={link.href}
										target={link.external ? "_blank" : undefined}
										rel={link.external ? "noopener noreferrer" : undefined}
										className="text-xs text-white/30 hover:text-white/60 transition-colors"
									>
										{link.label}
									</a>
								</li>
							))}
						</ul>
					</div>

					{/* Legal */}
					<div>
						<h4 className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-3">
							Legal
						</h4>
						<ul className="space-y-2">
							{[
								{
									label: "Privacy Policy",
									href: "/privacy/",
								},
								{
									label: "Terms of Service",
									href: "/terms/",
								},
							].map((item) => (
								<li key={item.label}>
									<a
										href={item.href}
										className="text-xs text-white/30 hover:text-white/60 transition-colors"
									>
										{item.label}
									</a>
								</li>
							))}
						</ul>
					</div>
				</div>

				<div className="border-t border-white/[0.06] mt-10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
					<p className="text-xs text-white/20">© {currentYear} Clapio. All rights reserved.</p>
					<p className="text-xs text-white/20">Built with ❤️ using Electron, React & TypeScript</p>
				</div>
				<div className="mt-4 flex justify-center">
					<a
						href="https://startupfa.me/s/clapio?utm_source=clapio.app"
						target="_blank"
						rel="noopener noreferrer"
					>
						<img
							src="https://startupfa.me/badges/featured-badge-small.webp"
							alt="Clapio - Featured on Startup Fame"
							width="224"
							height="36"
							loading="lazy"
							decoding="async"
						/>
					</a>
				</div>
			</div>
		</footer>
	);
}
